import {cn} from '@tempmee/core'
import {Box, Card, Center, CircularProgress, Column, Divider} from '@tempmee/react'
import {type HTMLAttributes, type ReactNode, forwardRef} from 'react'
import {EmptyState} from '../_internal/EmptyState'
import {SectionHeader} from '../_internal/SectionHeader'

export interface TableCardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Section title — e.g. "Recent shifts". */
  title: ReactNode
  /** Optional trailing action (e.g. an overflow menu, a filter, or a "View all" link). */
  action?: ReactNode
  /**
   * Optional toolbar region between the header and the table — a full-width row for
   * the app's search box and filter selects. The app owns the filter state.
   */
  toolbar?: ReactNode
  /** Show a centered spinner instead of the table body while data loads. @default false */
  loading?: boolean
  /** Show the empty placeholder instead of the table. @default false */
  empty?: boolean
  /** Message shown in the empty state. @default 'No records to display' */
  emptyMessage?: ReactNode
  /** Optional footer beneath the table — the app renders its own pagination here. */
  footer?: ReactNode
  /** The table — supplied by the app as `@tempmee/react` `Table` primitives. */
  children?: ReactNode
}

/**
 * TableCard — a card-wrapped table section: a padded `title` + `action` header, an
 * optional `toolbar` region (search + filters), the app-supplied `Table` in the
 * body, and an optional `footer` slot for pagination. Handles the loading and empty
 * states. Sorting, pagination, filtering, and row selection stay with the app —
 * this block only frames the table.
 *
 * Presentational-only. Composed from `Card`, `Column`, `Box`, `Divider`,
 * `Center`, `CircularProgress`, and the internal `SectionHeader` / `EmptyState`.
 */
export const TableCard = forwardRef<HTMLDivElement, TableCardProps>(
  (
    {
      title,
      action,
      toolbar,
      loading = false,
      empty = false,
      emptyMessage,
      footer,
      className,
      children,
      ...rest
    },
    ref,
  ) => (
    <Card ref={ref} className={cn('overflow-hidden p-0', className)} {...rest}>
      <Column>
        <Box className="px-4 pt-4 pb-3">
          <SectionHeader title={title} action={action} />
        </Box>

        {toolbar != null && <Box className="px-4 pb-3">{toolbar}</Box>}

        {loading ? (
          <Center className="min-h-40">
            <CircularProgress variant="indeterminate" size={32} />
          </Center>
        ) : empty ? (
          <EmptyState title={emptyMessage ?? 'No records to display'} />
        ) : (
          children
        )}

        {footer != null && (
          <>
            <Divider />
            <Box className="px-4 py-3">{footer}</Box>
          </>
        )}
      </Column>
    </Card>
  ),
)

TableCard.displayName = 'TableCard'
